import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import APIUtil from 'util/APIUtil';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import type {
  HomepageEntriesQuery,
  HomepageEntriesQueryVariables,
  HomepageEntriesQuery_entries_EntryNode as EntryNode,
  HomepageEntriesQuery_stageEntries_StageEntryNode as StageEntryNode,
} from './__generated/HomepageEntriesQuery';
import {reviewableRowProps} from './MyWorkRowHelpers';
import {MyWorkRowsList} from './MyWorkRowsList';
import {useMyWorkRows} from './useMyWorkRows';

type HomepageEntriesRowItem = EntryNode | StageEntryNode;

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

export const HOMEPAGE_ENTRIES_QUERY = gql`
  query HomepageEntriesQuery($entryIds: [StrictString!]!, $stageEntryIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NAVIGATION) {
    entries(ids: $entryIds) {
      ... on EntryNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
    stageEntries(ids: $stageEntryIds) {
      ... on StageEntryNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

/** Fetches and lists notebook entries and stage entries for the selected review-status filter. */
export default function EntriesRows({
  selectedEntryIds,
}: {
  selectedEntryIds: Array<string> | null;
}): JSX.Element {
  const [entryIds, stageEntryIds] = _.partition(selectedEntryIds ?? [], (id) => APIUtil.isEntry(id));

  const rowsResult = useMyWorkRows<
    HomepageEntriesQuery,
    HomepageEntriesQueryVariables,
    HomepageEntriesRowItem
  >({
    query: HOMEPAGE_ENTRIES_QUERY,
    variables: {entryIds, stageEntryIds},
    skip: selectedEntryIds == null,
    getItems: (data) => [...data.entries, ...data.stageEntries],
    toRowProps: (item) => reviewableRowProps(item),
    errorMessage: 'Error loading entries',
    testComponent: TEST_COMPONENT,
  });

  if (rowsResult.type !== QueryResultType.SUCCESS) {
    return rowsResult.element;
  }

  return (
    <MyWorkRowsList
      rows={rowsResult.rows}
      emptyMessage="No entries with selected statuses"
      emptyMessageDataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'messageRow',
        key: 'emptyEntries',
      }}
    />
  );
}
